import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import {html} from 'lit';
import {ModalStatus} from '@gl/lit/components/base/modal/index.js';
import type {
  AudienceExport,
  AudienceExportSchedule,
} from '@gl/lit/types/audience_exports.js';
import {CAMPAIGN_TYPE} from '@gl/lit/types/audience_exports.js';
import type AudienceExportEdit from './index.js';
import '../../base/form_input_field/index.js';
import type {FormSelectFieldOption} from '@gl/lit/components/base/form_select_field/index.js';
import {FormInputFieldType} from '../../base/form_input_field/index.js';
import type {CustomInputEvent} from '@gl/lit/components/base/input/index.js';
import {exportService} from '@gl/lit/services/exports.js';
import {getCSRFToken} from '@gl/lit/utils/auth.js';
import {UnreachableError} from '@gl/lit/utils/errors.js';
import {renderNotification} from '@gl/lit/utils/notifications.js';
import {AlertIntent} from '@gl/lit/components/base/alert/index.js';
import {AudienceExportTypes} from '@gl/lit/types/audiences.js';
import '../../base/feature_flags/index.js';
import {normalizeFrequency} from '../audience_export_schedule/utils.js';
import {isUndefined} from '@gl/lit/utils/index.js';

import '@gl/lit/components/audiences/audience_export_schedule/index.js';

export type Model = {
  activeModal: boolean;
  showEndDate: boolean;
  audienceExport: AudienceExport;
  initialAudienceExport: AudienceExport;
  frequencies: Record<string, string>;
  destinationName: string;
  destinationIcon: string;
  label: string;
  isExpired: boolean;
  isValid: boolean;
  submitting: boolean;
  errors: string;
  tableName?: string;
};

export const Msg = makeVariants({
  openModal: Variant<boolean>()('change-modal'),
  showEndDate: Variant<void>()('show-end-date'),
  hideEndDate: Variant<void>()('hide-end-date'),
  resetEndDate: Variant<void>()('reset-end-date'),
  audienceExportUpdated: Variant<Array<string>>()('audience-export-updated'),
  setEndDate: Variant<string>()('set-end-date'),
  setAudienceCampaignType: Variant<string>()('set-audience-campaign-type'),
  updateExport: Variant<void>()('update-export'),
  invalidate: Variant<void>()('invalidate-form'),
  validate: Variant<void>()('validate-form'),
  closeForm: Variant<void>()('close-form'),
  onSubmitSuccess: Variant<void>()('on-submit-success'),
  onSubmitFailure: Variant<string>()('on-submit-failure'),
  setFormSubmitting: Variant<boolean>()('set-form-submitting'),
  reloadTable: Variant<void>()('reload-table'),
  resetData: Variant<AudienceExportEditEvent>()('reset-data'),
  updateSchedule: Variant<AudienceExport['schedule']>()('update-schedule'),
});

export type Msg = VariantsFor<typeof Msg>;

export type CampaignTypes = 'ongoing' | 'one_time' | '';

export type AudienceExportEditEvent = {
  audienceExport: AudienceExport;
  destinationName: string;
  destinationIcon: string;
  frequencies: Record<string, string>;
  label: string;
  isExpired: boolean;
};

export const OPEN_EDIT_EXPORT_MODAL_EVENT = 'open-edit-export-modal';

export enum FormFieldKey {
  AUDIENCE_NAME = 'audience_name',
  EXPORT_TYPE = 'export_type',
  SYNC_FREQUENCY = 'sync_frequency',
  REQUIRE_END_DATE = 'require_end_date',
  ENDS_AT = 'ends_at',
}

const REQUIRED_KEYS: (keyof AudienceExport)[] = [
  'sync_frequency',
  'campaign_type',
];

const PROVIDER_LOGO_SVG_ICON_SIZE = 24;

function attachListeners(host: AudienceExportEdit) {
  addEventListener(
    OPEN_EDIT_EXPORT_MODAL_EVENT,
    (e: Event) => {
      const props = (<CustomEvent<AudienceExportEditEvent>>e).detail;
      host.handleUpdate(Msg.resetData(props));
    },
    {
      signal: host.disconnectSignal,
    }
  );
}

function init(props: InitialProperties, cmd: Cmd): ImmutableResult<Model> {
  const audienceExport = <AudienceExport>props.get('audienceExport');
  if (!audienceExport) {
    console.error('An export was not provided, component will not function');
  }
  if (audienceExport?.campaign_type === 'one time') {
    audienceExport.campaign_type = 'single_export';
  }

  const label = <string>props.get('label') ?? '';
  if (!label) console.error('A label is required');

  const frequencies = <Record<string, string>>props.get('frequencies') ?? {};
  if (!frequencies) console.error('Sync frequency is required');

  const destinationName = <string>props.get('destinationName') ?? '';
  if (!destinationName) console.error('Destination name is required');

  const destinationIcon = <string>props.get('destinationIcon') ?? '';
  if (!destinationIcon) console.error('Destination icon is required');

  const isExpired = <boolean>props.get('isExpired');

  const model = {
    activeModal: false,
    showEndDate: !!audienceExport?.ends_at,
    audienceExport: audienceExport,
    initialAudienceExport: structuredClone(audienceExport),
    frequencies: frequencies,
    destinationName: destinationName,
    destinationIcon: destinationIcon,
    label: label,
    isExpired: isExpired,
    isValid: false,
    submitting: false,
    errors: '',
    tableName: audienceExport?.settings?.table_name,
  };

  return [model, cmd(attachListeners)] as const;
}

function validate(host: AudienceExportEdit) {
  const allRequiredFieldsFilled = REQUIRED_KEYS.map(
    key =>
      !!host.model.audienceExport[key] &&
      (host.model.audienceExport[key] as string).length > 0
  ).every(presence => presence === true);
  allRequiredFieldsFilled
    ? host.handleUpdate(Msg.validate())
    : host.handleUpdate(Msg.invalidate());
}

function reloadTable() {
  const url = new URL(location.href);
  url.searchParams.set('state', 'exports');
  location.href = url.toString();
}

function setFormFields(host: AudienceExportEdit) {
  host.$syncFrequencySelect?.initialize(
    host.model.audienceExport.sync_frequency
  );

  host.$endDateSelect?.initialize(
    host.model.audienceExport?.ends_at ? 'yes' : 'no'
  );

  host.$endDate?.reset(host.model.audienceExport?.ends_at);
  validate(host);
}

// eslint-disable-next-line complexity
function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'reset-data':
      return [
        {
          ...model,
          audienceExport: msg.value.audienceExport,
          initialAudienceExport: structuredClone(msg.value.audienceExport),
          destinationName: msg.value.destinationName,
          destinationIcon: msg.value.destinationIcon,
          frequencies: msg.value.frequencies,
          label: msg.value.label,
          isExpired: msg.value.isExpired,
          activeModal: true,
          showEndDate: !!msg.value.audienceExport.ends_at,
          tableName: msg.value.audienceExport.settings?.table_name,
        },
        cmd(setFormFields),
      ] as const;
    case 'change-modal':
      return [
        {
          ...model,
          activeModal: msg.value,
        },
        cmd(validate),
      ] as const;
    case 'show-end-date': {
      return [
        {
          ...model,
          showEndDate: true,
        },
        cmd(validate),
      ] as const;
    }
    case 'hide-end-date': {
      return [
        {
          ...model,
          audienceExport: {
            ...model.audienceExport,
            ends_at: 'never',
          },
          showEndDate: false,
        },
        cmd(validate),
      ];
    }
    case 'reset-end-date': {
      return [
        {
          ...model,
          audienceExport: {
            ...model.audienceExport,
            ends_at: '',
          },
          showEndDate: false,
        },
        cmd(validate),
      ];
    }
    case 'update-schedule': {
      return [
        {
          ...model,
          audienceExport: {
            ...model.audienceExport,
            sync_frequency:
              msg.value?.frequency ?? model.audienceExport.sync_frequency,
            ends_at: msg.value?.end_time?.time ?? model.audienceExport.ends_at,
            schedule: msg.value,
          },
        },
        cmd(validate),
      ] as const;
    }
    case 'audience-export-updated':
      return [
        {
          ...model,
          audienceExport: {
            ...model.audienceExport,
            sync_frequency: msg.value[1],
          },
        },
        cmd(validate),
      ] as const;
    case 'set-audience-campaign-type': {
      const result = {
        ...model,
        audienceExport: {
          ...model.audienceExport,
          campaign_type: msg.value,
        },
      };

      if (model.audienceExport.schedule) {
        result.audienceExport.schedule = {
          ...model.audienceExport.schedule,
          schedule_type:
            msg.value === 'ongoing'
              ? CAMPAIGN_TYPE.ONGOING
              : CAMPAIGN_TYPE.ONE_TIME,
        };
      }

      return [result, cmd(validate)];
    }
    case 'set-end-date':
      return [
        {
          ...model,
          audienceExport: {
            ...model.audienceExport,
            ends_at: msg.value,
          },
        },
        cmd(validate),
      ] as const;
    case 'update-export':
      exportService.update(model.audienceExport, getCSRFToken());
      return [
        {
          ...model,
          activeModal: false,
        },
        cmd(validate),
      ];
    case 'validate-form':
      return {
        ...model,
        isValid: true,
      };
    case 'invalidate-form':
      return {
        ...model,
        isValid: false,
      };
    case 'close-form':
      return {
        ...model,
        activeModal: false,
      };
    case 'on-submit-success':
      renderNotification('export updated!', AlertIntent.SUCCESS);
      return [
        {
          ...model,
          showEndDate: false,
          activeModal: false,
        },
        cmd(reloadTable),
      ] as const;
    case 'on-submit-failure':
      renderNotification(msg.value, AlertIntent.DANGER);
      return {
        ...model,
        errors: msg.value,
      };
    case 'set-form-submitting':
      return {
        ...model,
        submitting: msg.value,
      };
    case 'reload-table':
      location.reload();
      return {
        ...model,
      };
  }
}

async function submitForm(host: AudienceExportEdit, model: Readonly<Model>) {
  if (!model.isValid) return;
  try {
    host.handleUpdate(Msg.setFormSubmitting(true));
    await exportService.update(model.audienceExport, getCSRFToken());
    host.handleUpdate(Msg.onSubmitSuccess);
  } catch (err) {
    host.handleUpdate(
      Msg.onSubmitFailure((err as Record<string, string>).error)
    );
  } finally {
    host.handleUpdate(Msg.setFormSubmitting(false));
  }
}

// -- View

function frequencyOptions(model: Model) {
  const options: Array<FormSelectFieldOption> = [];

  Object.keys(model.frequencies || {}).forEach(key => {
    options.push({
      value: key,
      label: model.frequencies[key],
    });
  }, []);

  return options;
}

function getModalLabel(model: Readonly<Model>) {
  return html`
    <div part="modal-header" class="modal__image">
      <div part="destination" class="modal__image--destination">
        <fly-icon
          .icon=${model.destinationIcon}
          ?svg=${true}
          svgIconSize=${PROVIDER_LOGO_SVG_ICON_SIZE}
        ></fly-icon>
      </div>
      <span>${model.destinationName}</span>
    </div>
  `;
}

function getInitialEndDate(model: Readonly<Model>): string {
  if (model.audienceExport.ends_at === 'never') return '';
  if (model.isExpired || !model.audienceExport.ends_at) {
    return addDaysToDateString(new Date().toISOString(), 30);
  }

  return model.audienceExport.ends_at;
}

function setEndDate(host: AudienceExportEdit, dateString: string) {
  host.handleUpdate(Msg.setEndDate(dateString + 'T23:59:59'));
  return dateString;
}

function addDaysToDateString(dateString: string, days: number) {
  const date = new Date(dateString);
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

function currentFormattedDate() {
  return new Date().toISOString().replace(/T.*/, '');
}

function granularExportForm(host: AudienceExportEdit, model: Readonly<Model>) {
  const {schedule} = model.audienceExport;
  return html`
    <audience-export-schedule
      exportedAudience
      campaign-type=${schedule?.schedule_type as CAMPAIGN_TYPE}
      frequency=${normalizeFrequency(schedule?.frequency ?? '')}
      start-type=${schedule?.start_type ?? ''}
      start-date=${schedule?.start_time?.time ?? ''}
      start-timezone=${schedule?.start_time?.timezone ?? ''}
      end-type=${schedule?.end_type ?? ''}
      end-date=${schedule?.end_time?.time ?? ''}
      end-timezone=${schedule?.end_time?.timezone ?? ''}
      .occurrence=${schedule?.occurrence}
      @export-schedule:change=${(evt: CustomEvent<AudienceExportSchedule>) => {
        host.handleUpdate(Msg.updateSchedule(evt.detail));
      }}
    ></audience-export-schedule>
  `;
}

function modalMarkup(host: AudienceExportEdit, model: Readonly<Model>) {
  const onModalToggle = (evt: ModalStatus) => {
    if (evt === ModalStatus.OPENED && !model.activeModal) {
      host.handleUpdate(Msg.openModal(true));
    }
    if (evt === ModalStatus.CLOSED && model.activeModal) {
      host.handleUpdate(Msg.openModal(false));
    }
  };

  function setAudienceCampaignType(evt: CustomEvent): void {
    const campaignType = evt.detail.value as CampaignTypes;
    switch (campaignType) {
      case 'ongoing':
        host.handleUpdate(
          Msg.setAudienceCampaignType(AudienceExportTypes.ONGOING)
        );
        break;
      case 'one_time':
        host.handleUpdate(
          Msg.setAudienceCampaignType(AudienceExportTypes.ONE_TIME)
        );
        break;
      case '':
        host.handleUpdate(Msg.setAudienceCampaignType(''));
        break;
      default:
        throw new UnreachableError(campaignType);
    }
  }

  return html`
    <fly-modal
      ?disablePrimaryAction=${!model.isValid || model.submitting}
      actions
      .label=${getModalLabel(model)}
      .okLabel=${model.submitting ? 'SUBMITTING' : 'OK'}
      ?open=${model.activeModal}
      ?preventCloseOnPrimaryAction=${true}
      @closed=${() => onModalToggle(ModalStatus.CLOSED)}
      @opened=${() => onModalToggle(ModalStatus.OPENED)}
      @ok=${() => submitForm(host, model)}
      @cancel=${() => host.handleUpdate(Msg.openModal(false))}
    >
      <div class="audience-export-edit__audience-name">
        ${model.audienceExport?.audience_name}
      </div>
      <br />

      <fly-form-radio-field
        name=${FormFieldKey.EXPORT_TYPE}
        data-type=${model.initialAudienceExport?.campaign_type}
        ?disabled=${model.initialAudienceExport?.campaign_type === 'ongoing'}
        label="Campaign Type"
        initial=${model.audienceExport?.campaign_type === 'ongoing'
          ? 'ongoing'
          : 'one_time'}
        required
        col=${2}
        hideFieldsetLabel
        @value=${setAudienceCampaignType}
      >
        <fly-radio
          label="Ongoing"
          value="ongoing"
          ?checked=${model.audienceExport?.campaign_type === 'ongoing'}
        ></fly-radio>
        <fly-radio
          label="One Time"
          value="one_time"
          ?checked=${model.audienceExport?.campaign_type !== 'ongoing'}
        ></fly-radio>
      </fly-form-radio-field>
      <br />

      ${renderSettings(model)}
      <fly-flag-boundary key="audienceScheduleOnExportModal" on>
        ${granularExportForm(host, model)}
      </fly-flag-boundary>

      <fly-flag-boundary key="audienceScheduleOnExportModal" off>
        <div class="audience-export-edit__label">Frequency</div>
        <fly-select
          id="sync-frequency-select"
          name=${FormFieldKey.SYNC_FREQUENCY}
          initial=${model.audienceExport?.sync_frequency}
          value=${model.audienceExport?.sync_frequency}
          minsize=${1}
          maxsize=${1}
          required
          @selected=${(event: CustomEvent) => {
            host.handleUpdate(
              Msg.audienceExportUpdated([
                FormFieldKey.SYNC_FREQUENCY,
                event.detail.value,
              ])
            );
          }}
        >
          ${Object.values(frequencyOptions(model)).map(frequency => {
            return html`
              <fly-list-item value="${frequency.value}"
                >${frequency.label}</fly-list-item
              >
            `;
          })}
        </fly-select>
        <br />
        ${setEndDateMarkup(model, host)}
        <div class="error-message">${model.errors}</div>
      </fly-flag-boundary>
    </fly-modal>
  `;
}

function renderSettings(model: Readonly<Model>) {
  if (isUndefined(model.tableName)) return ``;

  return html`<div class="settings-section">
    <div class="audience-export-edit__label">Table Name</div>
    <div
      class="audience-export-edit__table-name audience-export-edit__table-name--disabled"
    >
      ${model.tableName}
    </div>
  </div>`;
}

function setEndDateMarkup(model: Readonly<Model>, host: AudienceExportEdit) {
  if (model.audienceExport.campaign_type !== AudienceExportTypes.ONGOING) {
    return html``;
  }

  return html`<div class="audience-export-edit__label">
      When should sync end?
    </div>
    <fly-select
      id="end-date-select"
      name=${FormFieldKey.REQUIRE_END_DATE}
      initial=${JSON.stringify([model.audienceExport?.ends_at ? 'yes' : 'no'])}
      minsize=${1}
      maxsize=${1}
      @selected=${(event: CustomEvent) => {
        switch (event.detail.value) {
          case 'yes':
            host.handleUpdate(Msg.showEndDate());
            break;
          case 'no':
            host.handleUpdate(Msg.hideEndDate());
            break;
          case undefined:
            host.handleUpdate(Msg.resetEndDate());
            break;
        }
      }}
    >
      <fly-list-item value="no">Never</fly-list-item>
      <fly-list-item value="yes">On Specific Date</fly-list-item>
    </fly-select>
    <br />
    ${model.showEndDate
      ? html`
          <fly-form-input-field
            id="end-date"
            type=${FormInputFieldType.DATE}
            name=${FormFieldKey.ENDS_AT}
            initial=${getInitialEndDate(model)}
            label="End Date"
            .minDate=${currentFormattedDate()}
            @value=${(evt: CustomEvent<CustomInputEvent>) =>
              setEndDate(host, evt.detail.value)}
          ></fly-form-input-field>
        `
      : ''} `;
}

function view(this: AudienceExportEdit, model: Readonly<Model>) {
  if (!model.audienceExport) return html``;

  return modalMarkup(this, model);
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
